import { useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { getRole, updateRole, type Role } from '../../api/rolesApi.js';

export function RoleEditPage(): ReactNode {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { token } = useAuth();
  const [data, setData] = useState<Role | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!id) return;
    getRole(id, token)
      .then((r) => { if (alive) setData(r); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [id, token]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!data || !id) return;
    setError(null);
    setSaved(false);
    if (!data.roleName.trim()) {
      setError(t('roles.edit.err.name'));
      return;
    }
    setBusy(true);
    try {
      const updated = await updateRole(
        id,
        {
          roleName: data.roleName.trim(),
          roleDescription: data.roleDescription ?? undefined,
          version: data.version,
        },
        token,
      );
      setData(updated);
      setSaved(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (error && !data) return <p role="alert">{error}</p>;
  if (!data) return <p>{t('roles.edit.loading')}</p>;
  return (
    <main aria-label="role-edit">
      <h1>{t('roles.edit.title')}</h1>
      <form onSubmit={onSubmit} aria-label="role-edit-form" noValidate>
        <label htmlFor="re-code">{t('roles.edit.code')}</label>
        <input id="re-code" value={data.roleCode} readOnly />

        <label htmlFor="re-name">{t('roles.edit.name')}</label>
        <input
          id="re-name"
          value={data.roleName}
          onChange={(e) => setData({ ...data, roleName: e.target.value })}
        />

        <label htmlFor="re-desc">{t('roles.edit.description')}</label>
        <input
          id="re-desc"
          value={data.roleDescription ?? ''}
          onChange={(e) => setData({ ...data, roleDescription: e.target.value })}
        />

        {error ? <p role="alert">{error}</p> : null}
        {saved ? <p role="status">{t('roles.edit.saved')}</p> : null}
        <button type="submit" disabled={busy}>{t('roles.edit.save')}</button>
      </form>
    </main>
  );
}
